import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'sonner';
import { PlusIcon, PencilIcon, TrashIcon, UserIcon, MagnifyingGlassIcon, EyeIcon, EnvelopeIcon, ArrowUpTrayIcon, ChartBarIcon, ShoppingCartIcon, BanknotesIcon, CalendarIcon, ClockIcon, MapPinIcon, DocumentTextIcon, TruckIcon, ReceiptRefundIcon, ChevronUpIcon, ArrowPathIcon, WrenchIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from '../components/ui/navigation-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { format } from 'date-fns';
import { DatePicker } from '../components/ui/date-picker';
import PaginationFooter from '../components/PaginationFooter';
import ClientWhatsAppChatDialog from '../components/ClientWhatsAppChatDialog';
import { WHATSAPP_CHAT_ENABLED } from '../config/modules';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAppSocket } from '../lib/wesSocket';
import VehicleHistoryModal from '../components/VehicleHistoryModal';
import { confirmDialog } from '../components/ConfirmDialog';
import BillingProfiles from '../components/BillingProfiles';
import ResumenFlotaDialog from '../components/informes/ResumenFlotaDialog';
import { PersonasRetiroLista } from '../components/RetiroDialog';
import { Tip } from '../components/ui/tip';

interface Client {
  id: string;
  type: 'individual' | 'company';
  isFleet?: boolean;
  firstName?: string;
  lastName?: string;
  birthDate?: string;
  dni?: string;
  companyName?: string;
  cuit?: string;
  email: string;
  phone: string;
  whatsapp?: string;
  instagram?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  street?: string;
  streetNumber?: string;
  province?: string;
  country?: string;
  ivaCondition?: 'responsable_inscripto' | 'monotributista' | 'exento' | 'consumidor_final' | 'no_responsable' | 'responsable_no_inscripto';
  fiscalObligations?: string;
  activityCode?: string;
  taxId?: string;
  businessStartDate?: string;
  legalForm?: 'persona_fisica' | 'sociedad_anonima' | 'srl' | 'sociedad_colectiva' | 'cooperativa' | 'fundacion' | 'asociacion' | 'otro';
  category?: 'nuevo' | 'frecuente' | 'vip' | 'inactivo';
  totalSpent?: number;
  totalSpentComputed?: number;
  serviceCount?: number;
  visitCount?: number;
  lastServiceDate?: string;
  lastVisitDate?: string;
  isActive: boolean;
  contactPerson?: string;
  contactPhone?: string;
  vehicles?: Vehicle[];
  vehiclesCount?: number;
  debtBalance?: number;
  createdAt?: string;
  updatedAt?: string;
  notes?: string;
}

// Definicin de tipo para vehiculo
interface Vehicle {
  id?: string;
  make?: string;
  brand?: string;
  model: string;
  licensePlate: string;
  year: string;
  km?: string;
  mileage?: number;
  contactName?: string;
  contactPhone?: string;
  internalCode?: string;
}

const Clients: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [filters, setFilters] = useState<{
    search: string;
    type?: 'all' | 'individual' | 'company';
    category?: string;
    withVehicles?: 'all' | 'with' | 'without';
    activeWithinDays?: 'all' | '30' | '90';
    birthdayOnly?: boolean;
    noVisitsDays?: string;
    status?: 'all' | 'active' | 'inactive' | 'blocked';
  }>({
    search: '',
    type: 'all',
    withVehicles: 'all',
    activeWithinDays: 'all',
    birthdayOnly: false,
    noVisitsDays: '',
    status: 'all'
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editClient, setEditClient] = useState<Client | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailClient, setDetailClient] = useState<Client | null>(null);
  // Resumen mensual de flota (v12.3.0)
  const [resumenCliente, setResumenCliente] = useState<{ id: string; nombre: string } | null>(null);
  const [arcaLookupLoading, setArcaLookupLoading] = useState(false);
  const [vehiclePlateLookupLoading, setVehiclePlateLookupLoading] = useState<number | null>(null);
  const [arcaDocument, setArcaDocument] = useState('');
  const navigate = useNavigate();
  const [openVehiclesId, setOpenVehiclesId] = useState<string | null>(null);
  const location = useLocation();

  // Estados para paginacin
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Estados para el Menubar del modal de detalles
  const [activeSection, setActiveSection] = useState<'info' | 'vehicles' | 'appointments' | 'budgets' | 'services' | 'history'>('info');
  const [clientVehicles, setClientVehicles] = useState<Vehicle[]>([]);
  const [clientAppointments, setClientAppointments] = useState<any[]>([]);
  const [clientBudgets, setClientBudgets] = useState<any[]>([]);
  const [clientRemitos, setClientRemitos] = useState<any[]>([]);
  const [clientInvoices, setClientInvoices] = useState<any[]>([]);
  const [clientServiceReports, setClientServiceReports] = useState<any[]>([]);
  const [loadingSection, setLoadingSection] = useState(false);
  const [vehicleHistoryOpen, setVehicleHistoryOpen] = useState(false);
  const [selectedVehicleForHistory, setSelectedVehicleForHistory] = useState<any>(null);

  // Estados para el formulario de creacin
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showTaxInfoModal, setShowTaxInfoModal] = useState(false);
  const [showWhatsAppChat, setShowWhatsAppChat] = useState(false);
  const [chatClient, setChatClient] = useState<Client | null>(null);
  const openedFromQueryRef = useRef<string>('');

  useEffect(() => {
    const query = new URLSearchParams(location.search).get('q');
    if (query && query !== filters.search) {
      setFilters((prev) => ({ ...prev, search: query }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const clientId = params.get('clientId');
    const section = params.get('section');
    if (!clientId || clients.length === 0) return;

    const key = `${clientId}:${section || 'info'}`;
    if (openedFromQueryRef.current === key) return;

    const client = clients.find((c) => String(c.id) === String(clientId));
    if (!client) return;

    setDetailClient(client);
    setShowDetailModal(true);
    if (section && ['info', 'vehicles', 'appointments', 'budgets', 'services', 'history'].includes(section)) {
      setActiveSection(section as 'info' | 'vehicles' | 'appointments' | 'budgets' | 'services' | 'history');
    } else {
      setActiveSection('info');
    }
    openedFromQueryRef.current = key;
  }, [location.search, clients]);


  // Estado para vehiculos en el modal de creacin
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  
  // Estado para marcas de vehiculos
  const [vehicleBrands, setVehicleBrands] = useState<string[]>([]);
  const [filteredBrands, setFilteredBrands] = useState<string[]>([]);
  const [showBrandDropdown, setShowBrandDropdown] = useState<number | null>(null);

  // Funciones para manejar categorias
  const getCategoryColor = (category?: string) => {
    const colors = {
      'vip': 'bg-purple-100 text-purple-800',
      'frecuente': 'bg-blue-100 text-blue-800',
      'nuevo': 'bg-green-100 text-green-800',
      'inactivo': 'bg-gray-100 text-gray-800'
    };
    return colors[category as keyof typeof colors] || colors.nuevo;
  };

  const getCategoryText = (category?: string) => {
    const texts = {
      'vip': 'VIP',
      'frecuente': 'Frecuente',
      'nuevo': 'Nuevo',
      'inactivo': 'Inactivo'
    };
    return texts[category as keyof typeof texts] || 'Nuevo';
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  };

  const formatMileage = (mileage?: number | string) => {
    if (!mileage) return '-';
    const num = typeof mileage === 'string' ? parseInt(mileage) : mileage;
    if (isNaN(num)) return mileage.toString();
    return num.toLocaleString('es-AR');
  };

  const resolveVehicleId = (vehicle: any): string => {
    const rawId = vehicle?.id ?? vehicle?._id ?? vehicle?.vehicleId ?? vehicle?.vehicle_id ?? vehicle?.uuid;
    const normalizedId = String(rawId ?? '').trim();
    if (!normalizedId || normalizedId === 'undefined' || normalizedId === 'null' || normalizedId === 'NaN') {
      return '';
    }
    return normalizedId;
  };

  const openVehicleHistoryFromClientModal = (vehicle: any) => {
    const vehicleId = resolveVehicleId(vehicle);
    if (!vehicleId) {
      toast.error('No se pudo abrir el historial: vehículo sin identificador');
      return;
    }

    const name = detailClient ? getClientName(detailClient) : '';
    const targetPath = `/vehicles/${encodeURIComponent(vehicleId)}/history?clientName=${encodeURIComponent(name)}&from=${encodeURIComponent('/clients')}`;
    setShowDetailModal(false);
    setDetailClient(null);
    window.location.assign(targetPath);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-AR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatDateOnly = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('es-AR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  const formatShortDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('es-AR');
  };

  const getClientTotalSpent = (client: Client) =>
    client.totalSpentComputed ?? client.totalSpent ?? 0;

  const getClientTypeLabel = (client: Client) => {
    if (client.type === 'company') {
      return client.isFleet ? 'Empresa - Flota' : 'Empresa';
    }
    return 'Particular';
  };

  const getDaysSince = (dateString?: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    const diffMs = Date.now() - date.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  };

  const getLastVisitLabel = (dateString?: string | null) => {
    if (!dateString) return 'Sin visitas';
    const days = getDaysSince(dateString);
    if (days === null) return '-';
    if (days <= 0) return 'Hoy';
    if (days === 1) return 'Hace 1 dia';
    if (days < 30) return `Hace ${days} dias`;
    if (days < 365) return `Hace ${Math.round(days / 30)} meses`;
    return `Hace ${Math.round(days / 365)} anos`;
  };

  const getClientLevel = (client: Client) => {
    const days = getDaysSince(client.lastVisitDate || client.lastServiceDate || null);
    const visits = client.visitCount ?? client.serviceCount ?? 0;
    if (days === null) return 'inactivo';
    if (days <= 60 && visits >= 3) return 'frecuente';
    if (days <= 180) return 'ocasional';
    return 'inactivo';
  };

  const getClientLevelBadge = (level: string) => {
    const styles: Record<string, string> = {
      frecuente: 'bg-blue-100 text-blue-800',
      ocasional: 'bg-yellow-100 text-yellow-800',
      inactivo: 'bg-gray-100 text-gray-800'
    };
    return styles[level] || styles.inactivo;
  };

  const getValueIndicator = (totalSpent: number) => {
    if (totalSpent >= 500000) {
      return { label: 'Alto', className: 'bg-green-100 text-green-800' };
    }
    if (totalSpent >= 150000) {
      return { label: 'Medio', className: 'bg-amber-100 text-amber-800' };
    }
    if (totalSpent > 0) {
      return { label: 'Bajo', className: 'bg-red-100 text-red-800' };
    }
    return { label: 'Sin gasto', className: 'bg-gray-100 text-gray-800' };
  };

  const getSpentRange = (totalSpent: number) => {
    if (totalSpent <= 0) return 'Sin gasto';
    if (totalSpent <= 50000) return '0 - 50k';
    if (totalSpent <= 200000) return '50k - 200k';
    if (totalSpent <= 500000) return '200k - 500k';
    return '500k+';
  };

  const getClientStatus = (client: Client) => {
    if ((client.debtBalance || 0) > 0) return 'bloqueado';
    if (!client.isActive || client.category === 'inactivo') return 'inactivo';
    return 'activo';
  };

  const getClientStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      activo: 'bg-green-100 text-green-800',
      inactivo: 'bg-gray-100 text-gray-800',
      bloqueado: 'bg-red-100 text-red-800'
    };
    return styles[status] || styles.inactivo;
  };

  const isBirthdayThisMonth = (birthDate?: string) => {
    if (!birthDate) return false;
    const date = new Date(birthDate);
    if (Number.isNaN(date.getTime())) return false;
    const today = new Date();
    return date.getMonth() === today.getMonth();
  };

  const getClientLastVisitDate = (client: Client) =>
    client.lastVisitDate || client.lastServiceDate || null;

  const getGoogleMapsUrl = (address?: string, street?: string, streetNumber?: string, city?: string, province?: string) => {
    // Construir la direccion completa
    const addressParts: string[] = [];
    
    // Si hay address completo, usarlo directamente
    if (address) {
      addressParts.push(address);
    } else if (street) {
      // Si no hay address pero hay street, construir con street + streetNumber
      const streetAddress = streetNumber ? `${street} ${streetNumber}`.trim() : street;
      addressParts.push(streetAddress);
    }
    
    if (city) {
      addressParts.push(city);
    }
    
    if (province) {
      addressParts.push(province);
    }
    
    // Si no hay ninguna parte de la direccion, retornar vacio
    if (addressParts.length === 0) {
      return '';
    }
    
    const fullAddress = addressParts.join(', ');
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
  };

  // Funcin para descargar plantilla CSV
  const downloadTemplate = () => {
    const csvContent = `tipo;nombre;apellido;fechaNacimiento;dni;nombreEmpresa;cuit;email;telefono;whatsapp;direccion;ciudad;codigoPostal;calle;numero;provincia;pais;condicionIva;obligacionesFiscales;codigoActividad;idFiscal;fechaInicioActividad;formaLegal;esFlota;limiteCredito;descuento;notas;categoria;activo;personaContacto;telefonoContacto
"individual";"Juan";"Perez";"1985-03-15";"12345678";"";"";"juan.perez@email.com";"011-1234-5678";"5491112345678";"Av. Santa Fe 1234";"Buenos Aires";"1059";"Av. Santa Fe";"1234";"CABA";"Argentina";"consumidor_final";"";"";"";"";"persona_fisica";false;0;0;"Cliente frecuente";"nuevo";true;"";""
"company";"";"";"";"";"Empresa ABC S.A.";"20-12345678-9";"contacto@empresaabc.com";"011-2345-6789";"5491123456789";"Corrientes 567";"Buenos Aires";"1043";"Corrientes";"567";"CABA";"Argentina";"responsable_inscripto";"IVA";"123456";"20-12345678-9";"2010-01-01";"sociedad_anonima";true;100000;5;"Cliente empresarial";"nuevo";true;"Mara Gonzlez";"011-2345-6790"
"individual";"Ana";"Martnez";"1990-07-22";"87654321";"";"";"ana.martinez@email.com";"011-3456-7890";"5491134567890";"Belgrano 890";"Buenos Aires";"1090";"Belgrano";"890";"CABA";"Argentina";"monotributista";"";"";"";"";"persona_fisica";false;0;0;"Nueva cliente";"nuevo";true;"";""`;

    // Agregar BOM UTF-8 para que Excel reconozca la codificacin correctamente
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;
    
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'plantilla-clientes.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Funcin para manejar la subida de CSV
  const handleCsvUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Solo se permiten archivos CSV');
      return;
    }

    const formData = new FormData();
    formData.append('csvFile', file);

    try {
      setLoading(true);
      const response = await axios.post('/api/clients/upload-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const { summary, errors } = response.data;
      
      if (summary.success > 0) {
        toast.success(`Importacin exitosa: ${summary.success} clientes creados`);
        fetchClients(); // Recargar la lista
      }
      
      if (summary.errors > 0) {
        toast.error(`Errores en ${summary.errors} filas. Revisa la consola para ms detalles.`);
      }

      setShowUploadModal(false);
    } catch (error: any) {
      console.error('Error uploading CSV:', error);
      toast.error(error.response?.data?.error || 'Error al subir el archivo CSV');
    } finally {
      setLoading(false);
    }
  };

  // Estado para vehiculos en el modal de edicin
  const [editVehicles, setEditVehicles] = useState<Vehicle[]>([]);
  const [editAlsoProvider, setEditAlsoProvider] = useState(false);
  const [editPideDatosRetiro, setEditPideDatosRetiro] = useState(false);

  const { register, handleSubmit, reset, formState: { errors }, setValue, watch, control } = useForm();

  const handleArcaLookup = async () => {
    if (!arcaDocument.trim()) {
      toast.error('Ingres un DNI/CUIL/CUIT');
      return;
    }
    setArcaLookupLoading(true);
    try {
      const response = await axios.post(
        '/api/clients/search-afip',
        { document: arcaDocument.trim() },
        { timeout: 20000 }
      );
      const data = response.data?.data || {};
      const setVal = (field: string, value: any) => setValue(field as any, value, { shouldDirty: true, shouldValidate: true });

      // Limpiar documento (quitar caracteres no numéricos)
      const cleanDoc = (data.cuit || arcaDocument.trim()).replace(/\D/g, '');

      if (data.personType === 'JURIDICA' || data.companyName) {
        setVal('type', 'company');
        await new Promise(r => setTimeout(r, 50));
        setVal('companyName', data.companyName || '');
        setVal('cuit', cleanDoc);
        setVal('dni', '');
      } else {
        setVal('type', 'individual');
        await new Promise(r => setTimeout(r, 50));
        setVal('firstName', data.firstName || '');
        setVal('lastName', data.lastName || '');
        // Si es CUIT/CUIL (11 dígitos) guardarlo en cuit, si es DNI (8 dígitos) en dni
        if (cleanDoc.length === 11) {
          setVal('cuit', cleanDoc);
          setVal('dni', '');
        } else {
          setVal('dni', cleanDoc);
          setVal('cuit', '');
        }
      }

      if (data.ivaCondition) {
        setVal('ivaCondition', data.ivaCondition);
      }

      if (data.address) {
        setVal('street', data.address.street || '');
        setVal('streetNumber', data.address.streetNumber || '');
        setVal('city', data.address.city || '');
        setVal('province', data.address.province || '');
        setVal('postalCode', data.address.postalCode || '');
      }

      toast.success('Datos cargados desde ARCA');
    } catch (error: any) {
      console.error('Error buscando en ARCA:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('La consulta a ARCA tard demasiado. Intent de nuevo.');
      } else {
        toast.error(error.response?.data?.error || 'Error al buscar en ARCA');
      }
    } finally {
      setArcaLookupLoading(false);
    }
  };

  const fetchClients = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams();
          if (filters.search) params.append('search', filters.search);
    if (filters.type && filters.type !== 'all') params.append('type', filters.type);
    if (filters.category) params.append('category', filters.category);
      // Pedir todos los clientes para poder ordenar/paginar globalmente en el frontend
      params.append('limit', '10000');
      const response = await axios.get(`/api/clients?${params}`);
      setClients(response.data.clients || []);
    } catch (error) {
      toast.error('Error al cargar los clientes');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters]);

  const realtimeTimerRef = useRef<number | null>(null);
  const pendingRefreshRef = useRef(false);
  useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (
        path &&
        !(
          path.startsWith('/api/clients') ||
          path.startsWith('/api/vehicles') ||
          path.startsWith('/api/appointments') ||
          path.startsWith('/api/budgets') ||
          path.startsWith('/api/service-reports') ||
          path.startsWith('/api/remitos') ||
          path.startsWith('/api/invoices')
        )
      ) {
        return;
      }

      const modalOpen =
        showCreateModal ||
        showUploadModal ||
        showEditModal ||
        showDetailModal ||
        showAddressModal ||
        showTaxInfoModal;

      if (modalOpen) {
        pendingRefreshRef.current = true;
        return;
      }

      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        fetchClients({ silent: true });
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('cash:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('cash:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [fetchClients, showCreateModal, showUploadModal, showEditModal, showDetailModal, showAddressModal, showTaxInfoModal]);

  useEffect(() => {
    const modalOpen =
      showCreateModal ||
      showUploadModal ||
      showEditModal ||
      showDetailModal ||
      showAddressModal ||
      showTaxInfoModal;

    if (modalOpen) return;
    if (!pendingRefreshRef.current) return;
    pendingRefreshRef.current = false;
    fetchClients({ silent: true });
  }, [showCreateModal, showUploadModal, showEditModal, showDetailModal, showAddressModal, showTaxInfoModal, fetchClients]);

  const fetchVehicleBrands = useCallback(async () => {
    try {
      const response = await axios.get('/api/vehicles/brands');
      const brands = response.data.brands || [];
      setVehicleBrands(brands);
      setFilteredBrands(brands);
    } catch (error) {
      console.error('Error al cargar las marcas de vehiculos:', error);
    }
  }, []);

  const filterBrands = useCallback((searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredBrands(vehicleBrands);
    } else {
      const filtered = vehicleBrands.filter(brand =>
        brand.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredBrands(filtered);
    }
  }, [vehicleBrands]);

  useEffect(() => {
    fetchClients();
    fetchVehicleBrands();
  }, [fetchClients, fetchVehicleBrands]);

  const getClientName = (client: Client) => {
    return client.type === 'company'
      ? client.companyName || 'Empresa sin nombre'
      : `${client.firstName || ''} ${client.lastName || ''}`.trim();
  };

  // Formatear documento con tipo correcto (DNI, CUIL o CUIT)
  const formatDocument = (client: Client): string => {
    const doc = client.cuit || client.dni || '';
    if (!doc) return '-';

    const cleanDoc = doc.replace(/\D/g, '');

    // DNI: 8 dígitos
    if (cleanDoc.length === 8) {
      return `DNI ${cleanDoc}`;
    }

    // CUIT/CUIL: 11 dígitos
    if (cleanDoc.length === 11) {
      const prefix = cleanDoc.substring(0, 2);
      // CUIT de empresa: 30, 33, 34
      if (['30', '33', '34'].includes(prefix) || client.type === 'company') {
        return `CUIT ${cleanDoc}`;
      }
      // CUIL de persona: 20, 23, 24, 27
      return `CUIL ${cleanDoc}`;
    }

    // Si no coincide con ningún formato, mostrar como está
    return doc;
  };
  useEffect(() => {
    setCurrentPage(1);
  }, [
    filters.search,
    filters.type,
    filters.category,
    filters.withVehicles,
    filters.activeWithinDays,
    filters.birthdayOnly,
    filters.noVisitsDays,
    filters.status
  ]);

  // Calcular clientes filtrados y paginados
  const filteredClients = clients.filter((client) => {
    // Ocultar el cliente especial del sistema ("Consumidor Final")
    if ((client as any).isSystem) return false;

    const searchLower = filters.search.toLowerCase();
    const matchesSearch =
      !filters.search ||
      getClientName(client).toLowerCase().includes(searchLower) ||
      (client.cuit || client.dni || '').toLowerCase().includes(searchLower) ||
      (client.phone || '').toLowerCase().includes(searchLower) ||
      (client.vehicles || []).some((v: any) => (v.licensePlate || '').toLowerCase().includes(searchLower));

    const matchesType = !filters.type || filters.type === 'all' || client.type === filters.type;

    const vehiclesCount = client.vehicles ? client.vehicles.length : (client.vehiclesCount || 0);
    const matchesVehicles =
      filters.withVehicles === 'with'
        ? vehiclesCount > 0
        : filters.withVehicles === 'without'
          ? vehiclesCount === 0
          : true;

    const lastVisitDate = getClientLastVisitDate(client);
    const daysSinceVisit = getDaysSince(lastVisitDate);
    const activeWithinDays =
      filters.activeWithinDays && filters.activeWithinDays !== 'all'
        ? parseInt(filters.activeWithinDays, 10)
        : null;
    const matchesActiveWindow =
      activeWithinDays === null || activeWithinDays <= 0
        ? true
        : daysSinceVisit !== null && daysSinceVisit <= activeWithinDays;

    const noVisitsDays = filters.noVisitsDays ? parseInt(filters.noVisitsDays, 10) : null;
    const matchesNoVisits =
      noVisitsDays === null || noVisitsDays <= 0
        ? true
        : daysSinceVisit === null || daysSinceVisit >= noVisitsDays;

    const matchesBirthday = !filters.birthdayOnly || isBirthdayThisMonth(client.birthDate);

    const status = getClientStatus(client);
    const statusMap: Record<string, string> = {
      active: 'activo',
      inactive: 'inactivo',
      blocked: 'bloqueado'
    };
    const matchesStatus =
      !filters.status ||
      filters.status === 'all' ||
      status === statusMap[filters.status];

    return (
      matchesSearch &&
      matchesType &&
      matchesVehicles &&
      matchesActiveWindow &&
      matchesNoVisits &&
      matchesBirthday &&
      matchesStatus
    );
  });

  // Ordenamiento: por defecto alfabético; cuando hay búsqueda, prioriza matches por relevancia
  const sortedFilteredClients = (() => {
    const arr = filteredClients.slice();
    const query = (filters.search || '').trim().toLowerCase();
    const nameOf = (c: any) => (c.companyName || `${c.firstName || ''} ${c.lastName || ''}`).trim().toLowerCase();
    if (!query) {
      return arr.sort((a, b) => nameOf(a).localeCompare(nameOf(b), 'es', { sensitivity: 'base' }));
    }
    // Score más bajo = más relevante
    const score = (c: any) => {
      const n = nameOf(c);
      if (n.startsWith(query)) return 0;       // Nombre empieza con la búsqueda
      if (n.includes(query)) return 1;          // Nombre contiene la búsqueda
      const doc = (c.cuit || c.dni || '').toLowerCase();
      if (doc.includes(query)) return 2;        // Documento matchea
      return 3;                                  // Otro campo (teléfono, email, dirección)
    };
    return arr.sort((a, b) => {
      const sa = score(a);
      const sb = score(b);
      if (sa !== sb) return sa - sb;
      return nameOf(a).localeCompare(nameOf(b), 'es', { sensitivity: 'base' });
    });
  })();

  const totalPages = Math.ceil(sortedFilteredClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClients = sortedFilteredClients.slice(startIndex, endIndex);
  const totalResults = sortedFilteredClients.length;
  const startResult = sortedFilteredClients.length === 0 ? 0 : startIndex + 1;
  const endResult = Math.min(endIndex, sortedFilteredClients.length);

  // Funciones de navegacin de paginacin
  const goToFirstPage = () => setCurrentPage(1);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));
  const goToLastPage = () => setCurrentPage(totalPages);

  // Atajo de teclado para nuevo cliente
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      if (
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        target?.isContentEditable
      ) {
        return;
      }

      if (event.key.toLowerCase() === 'n') {
        event.preventDefault();
        navigate('/clients/new');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // Efecto para resetear el formulario cuando se abre el modal de edicin
  useEffect(() => {
    if (showEditModal && editClient) {
      reset(editClient);
    }
  }, [showEditModal, editClient, reset]);

  // Helper para limpiar y formatear nmeros
  const cleanPhone = (phone: string) => phone.replace(/[^0-9]/g, '').replace(/^549/, '').replace(/^54/, '');
  const getWhatsappNumber = (phone: string) => {
    const clean = cleanPhone(phone);
    return `549${clean}`;
  };
  const getChatPhone = (client: Client) => client.whatsapp || client.phone || '';

  const handleOpenWhatsAppChat = (client: Client) => {
    const phone = getChatPhone(client);
    if (!phone) {
      toast.error('El cliente no tiene teléfono/WhatsApp cargado');
      return;
    }
    // Chat interno apagado: el botón abre WhatsApp normal (wa.me)
    if (!WHATSAPP_CHAT_ENABLED) {
      const digits = String(phone).replace(/\D/g, '');
      window.open(`https://wa.me/${digits.startsWith('549') ? digits : `549${digits}`}`, '_blank');
      return;
    }
    setChatClient(client);
    setShowWhatsAppChat(true);
  };

  const normalizeArLicensePlate = (value: string) =>
    String(value || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');

  const isValidArLicensePlate = (value: string) => {
    const plate = normalizeArLicensePlate(value);
    return /^[A-Z]{3}\d{3}$/.test(plate) || /^[A-Z]{2}\d{3}[A-Z]{2}$/.test(plate);
  };

  const lookupEditVehiclePlate = async (idx: number, licensePlate: string) => {
    const plate = normalizeArLicensePlate(licensePlate);
    if (!plate || !isValidArLicensePlate(plate)) return;
    setVehiclePlateLookupLoading(idx);
    try {
      const res = await axios.get(`/api/vehicles/lookup-plate/${plate}`);
      const d = res.data;
      if (d.brand || d.make) updateEditVehicle(idx, 'brand', d.brand || d.make || '');
      if (d.model) updateEditVehicle(idx, 'model', d.model || '');
      if (d.year) updateEditVehicle(idx, 'year', String(d.year));
      toast.success(`Datos cargados: ${d.brand || d.make || ''} ${d.model || ''} ${d.year || ''}`);
    } catch (err: any) {
      if (err?.response?.status === 404) toast.info('Vehículo no encontrado en la base de datos');
      else toast.error(err?.response?.data?.error || 'Error consultando datos');
    } finally {
      setVehiclePlateLookupLoading(null);
    }
  };

  const normalizeVehicleYear = (value: string) => String(value || '').trim();

  const isValidVehicleYear = (value: string) => {
    const yearStr = normalizeVehicleYear(value);
    if (!yearStr) return true;
    const parsed = Number(yearStr);
    const currentYear = new Date().getFullYear();
    return Number.isFinite(parsed) && parsed >= 1900 && parsed <= currentYear;
  };

  // Funciones para agregar, editar y eliminar vehiculos en el modal de creacin
  const addVehicle = () => setVehicles([...vehicles, { brand: '', model: '', licensePlate: '', year: '', km: '' }]);
  const updateVehicle = (idx: number, field: keyof Vehicle, value: string) => {
    const updated = [...vehicles];
    if (field === 'brand') {
      updated[idx].brand = value;
      updated[idx].make = value; // Sincronizar con el campo del backend
      // Filtrar marcas mientras se escribe
      filterBrands(value);
      setShowBrandDropdown(idx);
    } else {
      updated[idx] = { ...updated[idx], [field]: value };
    }
    setVehicles(updated);
  };
  const removeVehicle = (idx: number) => setVehicles(vehicles.filter((_, i) => i !== idx));

  // Funciones para agregar, editar y eliminar vehiculos en el modal de edicin
  const addEditVehicle = () => setEditVehicles([...editVehicles, { brand: '', model: '', licensePlate: '', year: '', km: '', contactName: '', contactPhone: '', internalCode: '' }]);
  const updateEditVehicle = (idx: number, field: keyof Vehicle, value: string) => {
    const updated = [...editVehicles];
    if (field === 'brand') {
      updated[idx].brand = value;
      updated[idx].make = value; // Sincronizar con el campo del backend
      // Filtrar marcas mientras se escribe
      filterBrands(value);
      setShowBrandDropdown(idx);
    } else {
      updated[idx] = { ...updated[idx], [field]: value };
    }
    setEditVehicles(updated);
  };
  const removeEditVehicle = (idx: number) => setEditVehicles(editVehicles.filter((_, i) => i !== idx));

  const selectBrand = (brand: string, idx: number, isEdit: boolean = false) => {
    if (isEdit) {
      const updated = [...editVehicles];
      updated[idx].brand = brand;
      updated[idx].make = brand;
      setEditVehicles(updated);
    } else {
      const updated = [...vehicles];
      updated[idx].brand = brand;
      updated[idx].make = brand;
      setVehicles(updated);
    }
    setShowBrandDropdown(null);
    setFilteredBrands(vehicleBrands); // Resetear filtro
  };

  const handleBrandClick = (brand: string, idx: number, isEdit: boolean = false, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    selectBrand(brand, idx, isEdit);
  };


  // Resetear formulario cuando se cierra el modal
  useEffect(() => {
    if (!showCreateModal) {

      setShowAddressModal(false);
      setVehicles([]);
      setArcaDocument('');
      setArcaLookupLoading(false);
      reset();
    }
  }, [showCreateModal, reset]);

  const onSubmit = async (data: any) => {
    data.phone = cleanPhone(data.phone);
    
    // Si es empresa, usar razn social para nombre y apellido
    if (data.type === 'company' && data.companyName) {
      // Dividir la razn social en nombre y apellido (si tiene espacio)
      const parts = data.companyName.trim().split(' ');
      if (parts.length > 1) {
        data.firstName = parts[0];
        data.lastName = parts.slice(1).join(' ');
      } else {
        data.firstName = data.companyName;
        data.lastName = '';
      }
    }
    // Si es individual, los campos firstName y lastName ya vienen del formulario
    
    // Validar patentes (formato argentino) antes de enviar
    const normalizedVehicles = vehicles.map((v) => ({
      ...v,
      licensePlate: normalizeArLicensePlate(v.licensePlate),
      year: normalizeVehicleYear(v.year)
    }));
    const invalidVehicle = normalizedVehicles.find(
      (v) => v.licensePlate && !isValidArLicensePlate(v.licensePlate)
    );
    if (invalidVehicle) {
      toast.error('Formato de patente inválido. Ej: AAA123 o AB123CD');
      return;
    }
    const invalidYearVehicle = normalizedVehicles.find((v) => v.year && !isValidVehicleYear(v.year));
    if (invalidYearVehicle) {
      toast.error(`Año inválido. No puede ser mayor al actual (${new Date().getFullYear()}).`);
      return;
    }

    // Aviso de posible duplicado (best-effort, nunca bloquea si el chequeo falla)
    try {
      const dup = await axios.post('/api/clients/check-duplicate', {
        phone: data.phone, dni: data.dni, cuit: data.cuit,
        firstName: data.firstName, lastName: data.lastName, companyName: data.companyName
      });
      const matches = dup.data?.matches || [];
      if (matches.length > 0) {
        const lista = matches.slice(0, 3).map((m: any) => `${m.name} (${m.detalle})`).join(' · ');
        const ok = await confirmDialog({
          title: 'Posible cliente duplicado',
          description: `Ya existe: ${lista}. ¿Crear igual?`,
          confirmText: 'Crear igual',
          destructive: true
        });
        if (!ok) return;
      }
    } catch (_) { /* seguir con el alta */ }

    try {
      await axios.post('/api/clients', { ...data, vehicles: normalizedVehicles });
      toast.success('Cliente creado exitosamente');
      setShowCreateModal(false);
      setVehicles([]);

      reset();
      fetchClients();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al crear el cliente');
    }
  };

  const handleDelete = async (clientId: string) => {
    if (!await confirmDialog('Ests seguro de que quieres eliminar este cliente?')) return;
    try {
      await axios.delete(`/api/clients/${clientId}`);
      toast.success('Cliente eliminado exitosamente');
      fetchClients();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar el cliente');
    }
  };

  // Al abrir el modal de edicin, cargar los vehiculos del cliente (si los tuviera)
  const handleEdit = (client: Client) => {
    setEditClient(client);
    setShowEditModal(true);

    // Vínculo "también es proveedor" y "pedir quién retira": vienen del detalle, no de la lista
    setEditAlsoProvider(false);
    setEditPideDatosRetiro(false);
    axios.get(`/api/clients/${client.id}`)
      .then((res) => {
        setEditAlsoProvider(Boolean(res.data?.client?.linkedProvider));
        setEditPideDatosRetiro(Number(res.data?.client?.pideDatosRetiro) === 1);
      })
      .catch(() => {});

    // Usar los vehiculos que ya vienen con el cliente
    if (client.vehicles && client.vehicles.length > 0) {
      setEditVehicles(client.vehicles.map(v => ({
        id: v.id,
        brand: v.make || '',
        model: v.model || '',
        licensePlate: v.licensePlate || '',
        year: v.year || '',
        km: v.mileage?.toString() || '',
        contactName: (v as any).contactName || '',
        contactPhone: (v as any).contactPhone || '',
        internalCode: (v as any).internalCode || ''
      })));
    } else {
      setEditVehicles([]);
    }
    // El reset del formulario se maneja en el useEffect
  };

  const onEditSubmit = async (data: any) => {
    if (!editClient) return;
    data.phone = cleanPhone(data.phone);
    try {
      const normalizedVehicles = editVehicles.map((v) => ({
        ...v,
        licensePlate: normalizeArLicensePlate(v.licensePlate),
        year: normalizeVehicleYear(v.year)
      }));
      const invalidVehicle = normalizedVehicles.find(
        (v) => v.licensePlate && !isValidArLicensePlate(v.licensePlate)
      );
      if (invalidVehicle) {
        toast.error('Formato de patente inválido. Ej: AAA123 o AB123CD');
        return;
      }
      const invalidYearVehicle = normalizedVehicles.find((v) => v.year && !isValidVehicleYear(v.year));
      if (invalidYearVehicle) {
        toast.error(`Año inválido. No puede ser mayor al actual (${new Date().getFullYear()}).`);
        return;
      }

      const payload = { ...data, vehicles: normalizedVehicles, alsoProvider: editAlsoProvider, pideDatosRetiro: editPideDatosRetiro };
      const response = await axios.put(`/api/clients/${editClient.id}`, payload);
      toast.success('Cliente actualizado exitosamente');
      setShowEditModal(false);
      setEditClient(null);
      setEditVehicles([]);
      reset();
      fetchClients();
    } catch (error: any) {
      console.error('? [EDIT] Error al actualizar:', error);
      toast.error(error.response?.data?.error || 'Error al actualizar el cliente');
    }
  };

  const handleViewDetails = (client: Client) => {
    setDetailClient(client);
    setShowDetailModal(true);
    setActiveSection('info'); // Resetear a la seccin de informacin por defecto
  };

  // Funciones para obtener datos de cada seccin
  const fetchClientVehicles = async (clientId: string) => {
    try {
      setLoadingSection(true);
      const response = await axios.get(`/api/vehicles?clientId=${clientId}`);
      setClientVehicles(response.data || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setClientVehicles([]);
    } finally {
      setLoadingSection(false);
    }
  };

  const fetchClientAppointments = async (clientId: string) => {
    try {
      setLoadingSection(true);
      const response = await axios.get(`/api/appointments-enhanced?clientId=${clientId}`);
      setClientAppointments(response.data.appointments || response.data || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setClientAppointments([]);
    } finally {
      setLoadingSection(false);
    }
  };

  const fetchClientBudgets = async (clientId: string) => {
    try {
      setLoadingSection(true);
      const response = await axios.get(`/api/budgets?clientId=${clientId}`);
      setClientBudgets(response.data.budgets || response.data || []);
    } catch (error) {
      console.error('Error fetching budgets:', error);
      setClientBudgets([]);
    } finally {
      setLoadingSection(false);
    }
  };

  const fetchClientServices = async (clientId: string) => {
    try {
      setLoadingSection(true);
      const response = await axios.get(`/api/service-reports?clientId=${clientId}`);
      setClientServiceReports(response.data.reports || response.data || []);
    } catch (error) {
      console.error('Error fetching service reports:', error);
      setClientServiceReports([]);
    } finally {
      setLoadingSection(false);
    }
  };

  const fetchClientHistory = async (clientId: string) => {
    try {
      setLoadingSection(true);
      const [remitosRes, invoicesRes] = await Promise.all([
        axios.get(`/api/remitos?clientId=${clientId}`),
        axios.get(`/api/invoices?clientId=${clientId}`)
      ]);
      setClientRemitos(remitosRes.data.remitos || remitosRes.data || []);
      setClientInvoices(invoicesRes.data.invoices || invoicesRes.data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
      setClientRemitos([]);
      setClientInvoices([]);
    } finally {
      setLoadingSection(false);
    }
  };

  // useEffect para cargar datos cuando cambia la seccin activa
  useEffect(() => {
    if (!detailClient || !showDetailModal) return;

    switch (activeSection) {
      case 'vehicles':
        fetchClientVehicles(detailClient.id);
        break;
      case 'appointments':
        fetchClientAppointments(detailClient.id);
        break;
      case 'budgets':
        fetchClientBudgets(detailClient.id);
        break;
      case 'services':
        fetchClientServices(detailClient.id);
        break;
      case 'history':
        fetchClientHistory(detailClient.id);
        break;
      default:
        break;
    }
  }, [activeSection, detailClient, showDetailModal]);

  const handleSendEmail = (client: Client) => {
    if (client.email) {
      window.open(`mailto:${client.email}`, '_blank');
    } else {
      toast.error('Este cliente no tiene email registrado');
    }
  };



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-start">
        <h2 className="text-2xl font-bold tracking-tight">Clientes</h2>
        <div className="flex gap-2 flex-wrap">

              <Button
                onClick={() => setShowUploadModal(true)}
            variant="outline"
            className="hidden md:inline-flex items-center justify-center whitespace-nowrap text-sm font-medium h-8 rounded-lg gap-1.5 px-3 bg-card shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50"
            size="sm"
              >
            <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
            Importar
              </Button>
          <Button
            onClick={() => navigate('/clients/new')}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
            size="sm"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuevo cliente
            <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
              N
            </kbd>
              </Button>
            </div>
          </div>
{/* Buscador */}
            <div className="relative">
        <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
              <Input
                type="text"
          placeholder="Buscar por nombre, CUIT/CUIL, teléfono o patente..."
                value={filters.search}
                onChange={e => setFilters({ ...filters, search: e.target.value })}
          className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex min-w-0 rounded-lg border bg-white px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive h-8 w-full sm:w-[320px] lg:w-[520px] pl-8"
              />
            </div>

      {/* Filtros */}
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Tipo</Label>
          <Select
            value={filters.type || 'all'}
            onValueChange={(value) => setFilters({ ...filters, type: value as 'all' | 'individual' | 'company' })}
          >
            <SelectTrigger className="h-8 rounded-lg">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="individual">Particular</SelectItem>
              <SelectItem value="company">Empresa</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Vehículos</Label>
          <Select
            value={filters.withVehicles || 'all'}
            onValueChange={(value) => setFilters({ ...filters, withVehicles: value as 'all' | 'with' | 'without' })}
          >
            <SelectTrigger className="h-8 rounded-lg">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="with">Con vehiculos</SelectItem>
              <SelectItem value="without">Sin vehículos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Activos ultimos</Label>
          <Select
            value={filters.activeWithinDays || 'all'}
            onValueChange={(value) => setFilters({ ...filters, activeWithinDays: value as 'all' | '30' | '90' })}
          >
            <SelectTrigger className="h-8 rounded-lg">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="30">30 dias</SelectItem>
              <SelectItem value="90">90 dias</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Sin visitas (dias)</Label>
          <Input
            type="number"
            min="0"
            value={filters.noVisitsDays || ''}
            onChange={(e) => setFilters({ ...filters, noVisitsDays: e.target.value })}
            className="h-8 rounded-lg"
            placeholder="0"
          />
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Estado</Label>
          <Select
            value={filters.status || 'all'}
            onValueChange={(value) => setFilters({ ...filters, status: value as 'all' | 'active' | 'inactive' | 'blocked' })}
          >
            <SelectTrigger className="h-8 rounded-lg">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Activo</SelectItem>
              <SelectItem value="inactive">Inactivo</SelectItem>
              <SelectItem value="blocked">Bloqueado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Lista de clientes */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-12">
              <UserIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No hay clientes</p>
            </div>
          ) : (
            <>
              {/* Vista de tabla para pantallas medianas y grandes */}
              <div className="hidden md:block relative w-full overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-muted/50 border-b">
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Nombre</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Documento</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Telefono</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Visitas</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Nivel</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Total gastado</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Vehículos</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">ltima visita</TableHead>
                      <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedClients.map((client) => {
                      const totalSpent = getClientTotalSpent(client);
                      const level = getClientLevel(client);
                      const lastVisitDate = getClientLastVisitDate(client);
                      return (
                        <TableRow key={client.id} className="border-b hover:bg-muted/50 cursor-pointer">
                          <TableCell className="p-2 align-middle text-left max-w-[180px] cq-clamp-wide">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="font-medium truncate cursor-default">{getClientName(client)}</div>
                              </TooltipTrigger>
                              <TooltipContent side="top">{getClientName(client)}</TooltipContent>
                            </Tooltip>
                          </TableCell>
                          <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                            <div>{formatDocument(client)}</div>
                          </TableCell>
                          <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                            <div>{client.phone || '-'}</div>
                          </TableCell>
                          <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                            <div className="font-medium">{client.visitCount ?? client.serviceCount ?? 0}</div>
                          </TableCell>
                          <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                            <Badge className={getClientLevelBadge(level)}>{level}</Badge>
                          </TableCell>
                          <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                            <div className="font-medium">{getSpentRange(totalSpent)}</div>
                            <div className="text-xs text-muted-foreground">{formatCurrency(totalSpent)}</div>
                          </TableCell>
                          <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                            {client.vehicles && client.vehicles.length > 0 ? (
                              <HoverCard
                                open={openVehiclesId === client.id}
                                onOpenChange={(o) => setOpenVehiclesId(o ? client.id : null)}
                              >
                                <HoverCardTrigger asChild>
                                  <div
                                    className="cursor-pointer hover:underline"
                                    onClick={() => setOpenVehiclesId((prev) => (prev === client.id ? null : client.id))}
                                  >
                                    {client.vehicles.length} vehiculo(s)
                                  </div>
                                </HoverCardTrigger>
                                <HoverCardContent side="bottom" align="start" sideOffset={6} collisionPadding={8} className="w-[380px] max-w-[92vw] p-3">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead className="h-8 px-2 text-xs">Vehículo</TableHead>
                                        <TableHead className="h-8 px-2 text-xs">Patente</TableHead>
                                        <TableHead className="h-8 px-2 text-xs">Año</TableHead>
                                        <TableHead className="h-8 px-2 text-xs">KM</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {client.vehicles.map((vehicle, idx) => (
                                        <TableRow
                                          key={vehicle.id || idx}
                                          className="cursor-pointer hover:bg-accent/50"
                                          title="Ver historial del vehículo"
                                          onClick={() => {
                                            const vehicleId = resolveVehicleId(vehicle);
                                            if (!vehicleId) {
                                              toast.error('No se pudo abrir el historial: vehículo sin identificador');
                                              return;
                                            }
                                            const name = getClientName(client);
                                            navigate(`/vehicles/${encodeURIComponent(vehicleId)}/history?clientName=${encodeURIComponent(name)}&from=${encodeURIComponent('/clients')}`);
                                          }}
                                        >
                                          <TableCell className="px-2 py-1.5 text-xs font-medium align-top">
                                            <div className="max-w-[150px] whitespace-normal break-words leading-tight">
                                              {vehicle.make || vehicle.brand} {vehicle.model}
                                            </div>
                                          </TableCell>
                                          <TableCell className="px-2 py-1.5 text-xs font-semibold whitespace-nowrap align-top">
                                            {vehicle.licensePlate || '-'}
                                          </TableCell>
                                          <TableCell className="px-2 py-1.5 text-xs">
                                            {vehicle.year || '-'}
                                          </TableCell>
                                          <TableCell className="px-2 py-1.5 text-xs">
                                            {formatMileage(vehicle.mileage || vehicle.km)}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </HoverCardContent>
                              </HoverCard>
                            ) : (
                              <div>Sin vehículos</div>
                            )}
                          </TableCell>
                          <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                            <div className="font-medium">{getLastVisitLabel(lastVisitDate)}</div>
                            <div className="text-xs text-muted-foreground">{formatShortDate(lastVisitDate)}</div>
                          </TableCell>
                          <TableCell className="p-2 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end">
                              <ButtonGroup>
                                <Tip label="Ver detalles">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewDetails(client);
                                    }}
                                    className="size-8"
                                  >
                                    <EyeIcon className="h-4 w-4" />
                                  </Button>
                                </Tip>
                                <Tip label="Enviar email">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSendEmail(client);
                                    }}
                                    className="size-8"
                                  >
                                    <EnvelopeIcon className="h-4 w-4" />
                                  </Button>
                                </Tip>
                                <Tip label="WhatsApp">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenWhatsAppChat(client);
                                    }}
                                    className="size-8"
                                  >
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.030-.967-.273-.099-.471-.148-.670.150-.197.297-.767.966-.940 1.164-.173.199-.347.223-.644.075-.297-.150-1.255-.463-2.390-1.475-.883-.788-1.480-1.761-1.653-2.059-.173-.297-.018-.458.130-.606.134-.133.298-.347.446-.520.149-.174.198-.298.298-.497.099-.198.050-.371-.025-.520-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.500-.669-.510-.173-.008-.371-.010-.570-.010-.198 0-.520.074-.792.372-.272.297-1.040 1.016-1.040 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.200 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.360.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.570-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.510-5.260c.001-5.450 4.436-9.884 9.888-9.884 2.640 0 5.122 1.030 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.450-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.050 0C5.495 0 .160 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                                    </svg>
                                  </Button>
                                </Tip>
                                <Tip label="Eliminar">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(client.id);
                                    }}
                                    className="size-8"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </Button>
                                </Tip>
                              </ButtonGroup>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Vista de tarjetas para pantallas pequeas */}
              <div className="block md:hidden space-y-3">
                {paginatedClients.map((client) => {
                  const totalSpent = getClientTotalSpent(client);
                  const valueIndicator = getValueIndicator(totalSpent);
                  const level = getClientLevel(client);
                  const status = getClientStatus(client);
                  const lastVisitDate = getClientLastVisitDate(client);
                  const visitCount = client.visitCount ?? client.serviceCount ?? 0;
                  const vehiclesCount = client.vehicles ? client.vehicles.length : (client.vehiclesCount || 0);
                  return (
                    <Card key={client.id} className="shadow-sm hover:shadow-md transition-shadow duration-200">
                      <CardContent className="p-4">
                        <div className="mb-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-base truncate mb-1">{getClientName(client)}</h3>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge className={getCategoryColor(client.category)}>
                                  {getCategoryText(client.category)}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {getClientTypeLabel(client)}
                                </Badge>
                                <Badge className={getClientStatusBadge(status)}>{status}</Badge>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-end gap-1 mt-2">
                            <ButtonGroup>
                              <Tip label="Ver detalles">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => handleViewDetails(client)}
                                  className="size-8"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                              <Tip label="Enviar email">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => handleSendEmail(client)}
                                  className="size-8"
                                >
                                  <EnvelopeIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                              <Tip label="WhatsApp">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => handleOpenWhatsAppChat(client)}
                                  className="size-8"
                                >
                                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.030-.967-.273-.099-.471-.148-.670.150-.197.297-.767.966-.940 1.164-.173.199-.347.223-.644.075-.297-.150-1.255-.463-2.390-1.475-.883-.788-1.480-1.761-1.653-2.059-.173-.297-.018-.458.130-.606.134-.133.298-.347.446-.520.149-.174.198-.298.298-.497.099-.198.050-.371-.025-.520-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.500-.669-.510-.173-.008-.371-.010-.570-.010-.198 0-.520.074-.792.372-.272.297-1.040 1.016-1.040 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.200 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.360.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.570-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.510-5.260c.001-5.450 4.436-9.884 9.888-9.884 2.640 0 5.122 1.030 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.450-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.050 0C5.495 0 .160 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                                  </svg>
                                </Button>
                              </Tip>
                              <Tip label="Eliminar">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => handleDelete(client.id)}
                                  className="size-8"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                            </ButtonGroup>
                          </div>
                        </div>
                        <div className="space-y-2.5 text-sm border-t pt-3 mt-3">
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground font-medium">Documento:</span>
                            <span className="text-foreground font-medium">
                              {formatDocument(client)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground font-medium">Telefono:</span>
                            <span className="text-foreground">{client.phone || '-'}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground font-medium">ltima visita:</span>
                            <span className="text-foreground">{getLastVisitLabel(lastVisitDate)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground font-medium">Visitas:</span>
                            <span className="text-foreground">{visitCount}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground font-medium">Valor:</span>
                            <Badge className={valueIndicator.className}>{valueIndicator.label}</Badge>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground font-medium">Nivel:</span>
                            <Badge className={getClientLevelBadge(level)}>{level}</Badge>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground font-medium">Total gastado:</span>
                            <span className="text-foreground font-semibold">{getSpentRange(totalSpent)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground font-medium">Importe:</span>
                            <span className="text-foreground">{formatCurrency(totalSpent)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground font-medium">Vehículos:</span>
                            <span className="text-foreground">
                              {vehiclesCount > 0 ? `${vehiclesCount} vehiculo(s)` : 'Sin vehículos'}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
<PaginationFooter
        currentPage={currentPage}
        totalPages={totalPages}
        totalResults={totalResults}
        startResult={startResult}
        endResult={endResult}
        onFirst={goToFirstPage}
        onPrev={goToPreviousPage}
        onNext={goToNextPage}
        onLast={goToLastPage}
        isLoading={loading}
      />

      {/* Modal de creacin de cliente */}
      <Dialog open={showCreateModal} onOpenChange={(open) => !open && setShowCreateModal(false)}>
        <DialogContent className="max-w-3xl rounded-3xl border-0 shadow-2xl p-0 overflow-hidden">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Crear Cliente</DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 pb-6 overflow-y-auto max-h-[calc(90vh-140px)] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            <form id="create-client-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
                <div className="space-y-2">
                  <Label htmlFor="create-document" className="font-medium text-sm">Numero de Documento</Label>
                  <Input
                    id="create-document"
                    value={arcaDocument}
                    onChange={(event) => {
                      const value = event.target.value;
                      const cleanValue = value.replace(/\D/g, '');
                      setArcaDocument(value);
                      // Detectar automáticamente tipo de documento por longitud
                      if (cleanValue.length === 11) {
                        setValue('cuit', cleanValue);
                        setValue('dni', '');
                      } else if (cleanValue.length <= 8) {
                        setValue('dni', cleanValue);
                        setValue('cuit', '');
                      }
                    }}
                    className="px-4 py-3 rounded-xl border border-input bg-background"
                    placeholder="DNI / CUIL / CUIT"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    onClick={handleArcaLookup}
                    disabled={arcaLookupLoading || !arcaDocument.trim()}
                    className="h-10 rounded-xl px-4"
                  >
                    {arcaLookupLoading ? 'Buscando...' : 'Buscar en ARCA'}
                  </Button>
                </div>
              </div>
              <input type="hidden" {...register('dni')} />
              <input type="hidden" {...register('cuit')} />
              <input type="hidden" {...register('ivaCondition')} />
              <div className="space-y-2">
              <Label htmlFor="create-type" className="font-medium text-sm">Tipo *</Label>
              <Controller
                name="type"
                control={control}
                rules={{ required: 'Tipo es requerido' }}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className="h-10 rounded-xl">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="company">Empresa</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.type && (
                <p className="text-sm text-red-600 mt-1">{errors.type.message as string}</p>
              )}
            </div>

            {/* Nombre, Apellido y Razon Social */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-firstName" className="font-medium text-sm">Nombre</Label>
                <Input
                  id="create-firstName"
                  {...register('firstName', { required: watch('type') === 'individual' ? 'Nombre es requerido' : false })}
                  className={`px-4 py-3 rounded-xl border border-input bg-background ${errors.firstName ? 'border-red-500' : ''}`}
                  placeholder="Juan"
                />
                {errors.firstName && (
                  <p className="text-sm text-red-600 mt-1">{errors.firstName.message as string}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-lastName" className="font-medium text-sm">Apellido</Label>
                <Input
                  id="create-lastName"
                  {...register('lastName', { required: watch('type') === 'individual' ? 'Apellido es requerido' : false })}
                  className={`px-4 py-3 rounded-xl border border-input bg-background ${errors.lastName ? 'border-red-500' : ''}`}
                  placeholder="Perez"
                />
                {errors.lastName && (
                  <p className="text-sm text-red-600 mt-1">{errors.lastName.message as string}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-companyName" className="font-medium text-sm">
                Razon Social {watch('type') === 'company' && <span className="text-red-500">*</span>}
              </Label>
              <Input
                id="create-companyName"
                {...register('companyName', { required: watch('type') === 'company' ? 'La razn social es requerida' : false })}
                className={`px-4 py-3 rounded-xl border border-input bg-background ${errors.companyName ? 'border-red-500' : ''}`}
                placeholder="Ej: Empresa S.A."
              />
              {errors.companyName && (
                <p className="text-sm text-red-600 mt-1">{errors.companyName.message as string}</p>
              )}
            </div>

            {/* Email y Telefono */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-email" className="font-medium text-sm">Email</Label>
                <Input
                  id="create-email"
                  {...register('email')}
                  className="px-4 py-3 rounded-xl border border-input bg-background"
                  placeholder="correo@ejemplo.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-phone" className="font-medium text-sm">Telefono</Label>
                <Input
                  id="create-phone"
                  {...register('phone')}
                  className="px-4 py-3 rounded-xl border border-input bg-background"
                  placeholder="11-1234-5678"
                />
              </div>
            </div>

            {/* Fecha de nacimiento - Solo para individuos */}
            {watch('type') === 'individual' && (
              <div className="space-y-2">
                <Label htmlFor="create-birthDate" className="font-medium text-sm">Fecha de nacimiento</Label>
                <Controller
                  name="birthDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      id="create-birthDate"
                      value={field.value}
                      onChange={field.onChange}
                      fromYear={1900}
                      toYear={new Date().getFullYear()}
                    />
                  )}
                />
              </div>
            )}

            {/* Información impositiva */}
            <div className="rounded-lg border bg-card p-4">
              <div className="grid grid-cols-3 items-center gap-4 text-sm md:grid-cols-5">
                <div>
                  <div className="mb-1 text-muted-foreground">Tipo</div>
                  <div className="font-medium">
                    {watch('type') === 'company' ? 'CUIT' : 'CUIL'}
                  </div>
                  <input type="hidden" value={watch('type') === 'company' ? 'CUIT' : 'CUIL'} name="taxIdType" />
                </div>
                <div>
                  <div className="mb-1 text-muted-foreground">Personería</div>
                  <div className="font-medium">
                    {watch('type') === 'company' ? 'Jurídica' : 'Física'}
                  </div>
                  <input type="hidden" value={watch('type') === 'company' ? 'JURIDICA' : 'FISICA'} name="personType" />
                </div>
                <div className="col-span-2">
                  <div className="mb-1 text-muted-foreground">Categoria</div>
                  <div className="font-medium">
                    {watch('ivaCondition') === 'responsable_inscripto' ? 'Responsable Inscripto' :
                     watch('ivaCondition') === 'monotributista' ? 'Monotributista' :
                     watch('ivaCondition') === 'exento' ? 'Exento' :
                     watch('ivaCondition') === 'consumidor_final' ? 'Consumidor Final' :
                     watch('ivaCondition') === 'no_responsable' ? 'No Responsable' :
                     watch('ivaCondition') === 'responsable_no_inscripto' ? 'Responsable No Inscripto' :
                     'Consumidor Final'}
                  </div>
                  <input type="hidden" value={watch('ivaCondition') || 'consumidor_final'} name="taxCategory" />
                </div>
                <button
                  type="button"
                  onClick={() => setShowTaxInfoModal(true)}
                  className="inline-flex shrink-0 cursor-pointer items-center whitespace-nowrap font-medium outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 text-primary underline-offset-4 hover:underline h-8 gap-1.5 rounded-lg has-[>svg]:px-2.5 justify-end px-0 text-xs"
                >
                  <PencilIcon className="size-3" />
                  Editar
                </button>
              </div>
            </div>

            {/* Boton para agregar direccion */}
            <div className="border-t pt-4">
              <button
                type="button"
                onClick={() => setShowAddressModal(true)}
                className="shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 h-9 px-4 py-2 has-[>svg]:px-3 flex w-full justify-start text-left"
              >
                <PlusIcon className="size-4" />
                Agregar direccion
              </button>
            </div>

            </form>
          </div>
          <div className="px-6 py-4 border-t bg-muted/30">
            <div className="flex justify-end gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowCreateModal(false)}
                className="rounded-xl bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                form="create-client-form"
                className="rounded-xl bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-white/90 text-white dark:text-black shadow-sm"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Crear Cliente
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mini Modal de Agregar Direccion */}
      <Dialog open={showAddressModal} onOpenChange={(open) => setShowAddressModal(open)}>
        <DialogContent className="max-w-lg rounded-3xl border-0 shadow-2xl p-0 overflow-hidden">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Agregar direccion</DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 pb-6">
            <form onSubmit={(e) => { e.preventDefault(); setShowAddressModal(false); }} className="space-y-4">
              {/* Calle */}
              <div className="space-y-2">
                <Label htmlFor="modal-street" className="font-medium text-sm">Calle</Label>
                <Input
                  id="modal-street"
                  {...register('street')}
                  className="px-4 py-3 rounded-xl border border-input bg-background"
                  placeholder="Av. Corrientes 1234"
                />
              </div>

              {/* Depto/Piso y Cdigo Postal */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="modal-streetNumber" className="font-medium text-sm">Depto/Piso</Label>
                  <Input
                    id="modal-streetNumber"
                    {...register('streetNumber')}
                    className="px-4 py-3 rounded-xl border border-input bg-background"
                    placeholder="4 B"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modal-postalCode" className="font-medium text-sm">Cdigo Postal</Label>
                  <Input
                    id="modal-postalCode"
                    {...register('postalCode')}
                    className="px-4 py-3 rounded-xl border border-input bg-background"
                    placeholder="1234"
                  />
                </div>
              </div>

              {/* Provincia y Ciudad */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="modal-province" className="font-medium text-sm">Provincia</Label>
                  <Controller
                    name="province"
                    control={control}
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger className="h-10 rounded-xl">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Buenos Aires">Buenos Aires</SelectItem>
                          <SelectItem value="Crdoba">Crdoba</SelectItem>
                          <SelectItem value="Santa Fe">Santa Fe</SelectItem>
                          <SelectItem value="Mendoza">Mendoza</SelectItem>
                          <SelectItem value="Tucumn">Tucumn</SelectItem>
                          <SelectItem value="Entre Ros">Entre Ros</SelectItem>
                          <SelectItem value="Salta">Salta</SelectItem>
                          <SelectItem value="Misiones">Misiones</SelectItem>
                          <SelectItem value="Chaco">Chaco</SelectItem>
                          <SelectItem value="Corrientes">Corrientes</SelectItem>
                          <SelectItem value="Santiago del Estero">Santiago del Estero</SelectItem>
                          <SelectItem value="San Juan">San Juan</SelectItem>
                          <SelectItem value="Jujuy">Jujuy</SelectItem>
                          <SelectItem value="Ro Negro">Ro Negro</SelectItem>
                          <SelectItem value="Formosa">Formosa</SelectItem>
                          <SelectItem value="Neuquen">Neuquen</SelectItem>
                          <SelectItem value="Chubut">Chubut</SelectItem>
                          <SelectItem value="San Luis">San Luis</SelectItem>
                          <SelectItem value="Catamarca">Catamarca</SelectItem>
                          <SelectItem value="La Rioja">La Rioja</SelectItem>
                          <SelectItem value="La Pampa">La Pampa</SelectItem>
                          <SelectItem value="Santa Cruz">Santa Cruz</SelectItem>
                          <SelectItem value="Tierra del Fuego">Tierra del Fuego</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modal-city" className="font-medium text-sm">Ciudad</Label>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger className="h-10 rounded-xl">
                          <SelectValue placeholder="Seleccionar ciudad" />
                        </SelectTrigger>
                        <SelectContent>
                          {/* Las ciudades se pueden cargar dinamicamente segun la provincia seleccionada */}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </div>

              {/* Pas */}
              <div className="space-y-2">
                <Label htmlFor="modal-country" className="font-medium text-sm">Pas</Label>
                <Input
                  id="modal-country"
                  {...register('country')}
                  defaultValue="Argentina"
                  className="px-4 py-3 rounded-xl border border-input bg-background"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddressModal(false)}
                  className="rounded-xl bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="rounded-xl bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-white/90 text-white dark:text-black shadow-sm"
                >
                  Guardar
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Editar Información impositiva */}
      <Dialog open={showTaxInfoModal} onOpenChange={(open) => setShowTaxInfoModal(open)}>
        <DialogContent className="max-w-lg rounded-3xl border-0 shadow-2xl p-0 overflow-hidden">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Editar Información impositiva</DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 pb-6">
            <form onSubmit={(e) => { e.preventDefault(); setShowTaxInfoModal(false); }} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="modal-ivaCondition" className="font-medium text-sm">Condición IVA</Label>
                <Controller
                  name="ivaCondition"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className="h-10 rounded-xl">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="responsable_inscripto">Responsable Inscripto</SelectItem>
                        <SelectItem value="monotributista">Monotributista</SelectItem>
                        <SelectItem value="exento">Exento</SelectItem>
                        <SelectItem value="consumidor_final">Consumidor Final</SelectItem>
                        <SelectItem value="no_responsable">No Responsable</SelectItem>
                        <SelectItem value="responsable_no_inscripto">Responsable No Inscripto</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              {watch('type') === 'company' && (
                <div className="space-y-2">
                  <Label htmlFor="modal-legalForm" className="font-medium text-sm">Forma Legal</Label>
                  <Controller
                    name="legalForm"
                    control={control}
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger className="h-10 rounded-xl">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="persona_fisica">Persona Física</SelectItem>
                          <SelectItem value="sociedad_anonima">Sociedad Annima</SelectItem>
                          <SelectItem value="srl">SRL</SelectItem>
                          <SelectItem value="sociedad_colectiva">Sociedad Colectiva</SelectItem>
                          <SelectItem value="cooperativa">Cooperativa</SelectItem>
                          <SelectItem value="fundacion">Fundacin</SelectItem>
                          <SelectItem value="asociacion">Asociacin</SelectItem>
                          <SelectItem value="otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowTaxInfoModal(false)}
                  className="rounded-xl bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="rounded-xl bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-white/90 text-white dark:text-black shadow-sm"
                >
                  Guardar
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de edicin de cliente */}
      <Dialog open={showEditModal && editClient !== null} onOpenChange={(open) => { if (!open) { setShowEditModal(false); setEditClient(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Editar Cliente</DialogTitle>
          </DialogHeader>
          <form id="edit-client-form" onSubmit={handleSubmit(onEditSubmit)} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-type" className="font-medium text-sm">Tipo *</Label>
                  <select
                    id="edit-type"
                    {...register('type', { required: 'Tipo es requerido' })}
                    className="flex h-10 w-full rounded-lg border border-input bg-background px-4 py-3 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Seleccionar...</option>
                    <option value="individual">Individual</option>
                    <option value="company">Empresa</option>
                  </select>
                  {typeof errors.type?.message === 'string' && <p className="text-sm text-red-600 mt-1">{errors.type.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email" className="font-medium text-sm">Email *</Label>
                  <Input
                    id="edit-email"
                    {...register('email', { required: 'Email es requerido' })}
                    className="px-4 py-3 rounded-lg"
                    placeholder="correo@ejemplo.com"
                  />
                  {typeof errors.email?.message === 'string' && <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-phone" className="font-medium text-sm">Telefono *</Label>
                  <Input
                    id="edit-phone"
                    {...register('phone', { required: 'Telefono es requerido' })}
                    className="px-4 py-3 rounded-lg"
                    placeholder="11 1234-5678"
                  />
                  {typeof errors.phone?.message === 'string' && <p className="text-sm text-red-600 mt-1">{errors.phone.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-firstName" className="font-medium text-sm">Nombre</Label>
                  <Input
                    id="edit-firstName"
                    {...register('firstName')}
                    className="px-4 py-3 rounded-lg"
                    placeholder="Nombre (si es individual)"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-lastName" className="font-medium text-sm">Apellido</Label>
                  <Input
                    id="edit-lastName"
                    {...register('lastName')}
                    className="px-4 py-3 rounded-lg"
                    placeholder="Apellido (si es individual)"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-instagram" className="font-medium text-sm">Instagram</Label>
                  <Input
                    id="edit-instagram"
                    {...register('instagram')}
                    className="px-4 py-3 rounded-lg"
                    placeholder="@usuario"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-birthDate" className="font-medium text-sm">Fecha de Nacimiento</Label>
                  <Controller
                    name="birthDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        id="edit-birthDate"
                        value={field.value || ''}
                        onChange={field.onChange}
                        className="px-4 py-3 rounded-lg"
                        fromYear={1900}
                        toYear={new Date().getFullYear()}
                      />
                    )}
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="edit-companyName" className="font-medium text-sm">Nombre de Empresa</Label>
                  <Input
                    id="edit-companyName"
                    {...register('companyName')}
                    className="px-4 py-3 rounded-lg"
                    placeholder="Nombre de empresa (si es empresa)"
                  />
                </div>
              </div>

              {/* Campos Fiscales - Modal Edicin */}
              <div className="border-t pt-6 mt-6 space-y-4">
                <h4 className="font-semibold text-base text-foreground">Información fiscal</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-dni" className="font-medium text-sm">DNI/CUIT</Label>
                    <Input
                      id="edit-dni"
                      {...register('dni')}
                      className="px-4 py-3 rounded-lg"
                      placeholder="DNI (individual) o CUIT (empresa)"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-ivaCondition" className="font-medium text-sm">Condición IVA</Label>
                    <select
                      id="edit-ivaCondition"
                      {...register('ivaCondition')}
                      className="flex h-10 w-full rounded-lg border border-input bg-background px-4 py-3 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Seleccionar...</option>
                      <option value="responsable_inscripto">Responsable Inscripto</option>
                      <option value="monotributista">Monotributista</option>
                      <option value="exento">Exento</option>
                      <option value="consumidor_final">Consumidor Final</option>
                      <option value="no_responsable">No Responsable</option>
                      <option value="responsable_no_inscripto">Responsable No Inscripto</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-legalForm" className="font-medium text-sm">Forma Legal</Label>
                    <select
                      id="edit-legalForm"
                      {...register('legalForm')}
                      className="flex h-10 w-full rounded-lg border border-input bg-background px-4 py-3 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Seleccionar...</option>
                      <option value="persona_fisica">Persona Física</option>
                      <option value="sociedad_anonima">Sociedad Annima</option>
                      <option value="srl">SRL</option>
                      <option value="sociedad_colectiva">Sociedad Colectiva</option>
                      <option value="cooperativa">Cooperativa</option>
                      <option value="fundacion">Fundacin</option>
                      <option value="asociacion">Asociacin</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-activityCode" className="font-medium text-sm">Cdigo de Actividad</Label>
                    <Input
                      id="edit-activityCode"
                      {...register('activityCode')}
                      className="px-4 py-3 rounded-lg"
                      placeholder="Cdigo de actividad econmica"
                    />
                  </div>
                </div>
              </div>

              {/* Dirección fiscal - Modal Edicin */}
              <div className="border-t pt-6 mt-6 space-y-4">
                <h4 className="font-semibold text-base text-foreground">Dirección fiscal</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-street" className="font-medium text-sm">Calle</Label>
                    <Input
                      id="edit-street"
                      {...register('street')}
                      className="px-4 py-3 rounded-lg"
                      placeholder="Nombre de la calle"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-streetNumber" className="font-medium text-sm">Numero</Label>
                    <Input
                      id="edit-streetNumber"
                      {...register('streetNumber')}
                      className="px-4 py-3 rounded-lg"
                      placeholder="Numero"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-city" className="font-medium text-sm">Ciudad</Label>
                    <Input
                      id="edit-city"
                      {...register('city')}
                      className="px-4 py-3 rounded-lg"
                      placeholder="Ciudad"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-province" className="font-medium text-sm">Provincia</Label>
                    <Input
                      id="edit-province"
                      {...register('province')}
                      className="px-4 py-3 rounded-lg"
                      placeholder="Provincia"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-country" className="font-medium text-sm">Pas</Label>
                    <Input
                      id="edit-country"
                      {...register('country')}
                      className="px-4 py-3 rounded-lg"
                      placeholder="Pas"
                      defaultValue="Argentina"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-postalCode" className="font-medium text-sm">Cdigo Postal</Label>
                    <Input
                      id="edit-postalCode"
                      {...register('postalCode')}
                      className="px-4 py-3 rounded-lg"
                      placeholder="Cdigo postal"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-6 mt-6 space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold text-base text-foreground">Vehículos</h4>
                  <Button type="button" variant="outline" size="sm" onClick={addEditVehicle}>
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Agregar Vehculo
                  </Button>
                </div>
                {editVehicles.length === 0 && <p className="text-muted-foreground text-sm">No hay vehículos cargados</p>}
                {editVehicles.map((v, idx) => (
                  <div key={idx} className="grid grid-cols-5 gap-3 p-4 border rounded-lg bg-muted/30 items-end relative">
                    <div className="relative space-y-2">
                      <Label className="font-medium text-sm">Marca</Label>
                      <Input
                        className="px-4 py-2 rounded-lg"
                        placeholder="Marca"
                        value={v.brand}
                        onChange={e => updateEditVehicle(idx, 'brand', e.target.value)}
                        onFocus={() => setShowBrandDropdown(idx)}
                        onBlur={() => setTimeout(() => setShowBrandDropdown(null), 150)}
                      />
                      {showBrandDropdown === idx && filteredBrands.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-background border border-input rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {filteredBrands.map((brand, brandIdx) => (
                            <div
                              key={brandIdx}
                              className="px-3 py-2 hover:bg-muted cursor-pointer text-sm transition-colors"
                              onMouseDown={(e) => handleBrandClick(brand, idx, true, e)}
                            >
                              {brand}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium text-sm">Modelo</Label>
                      <Input className="px-4 py-2 rounded-lg" placeholder="Modelo" value={v.model} onChange={e => updateEditVehicle(idx, 'model', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium text-sm">Patente</Label>
                      <div className="flex gap-2">
                        <Input
                          className="px-4 py-2 rounded-lg flex-1"
                          placeholder="Patente"
                          value={v.licensePlate}
                          onChange={e => updateEditVehicle(idx, 'licensePlate', e.target.value.toUpperCase())}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                          disabled={vehiclePlateLookupLoading === idx || !isValidArLicensePlate(v.licensePlate)}
                          onClick={() => lookupEditVehiclePlate(idx, v.licensePlate)}
                        >
                          {vehiclePlateLookupLoading === idx ? (
                            <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full inline-block" />
                          ) : 'Buscar'}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium text-sm">Ao</Label>
                      <Input className="px-4 py-2 rounded-lg" placeholder="Ao" value={v.year} onChange={e => updateEditVehicle(idx, 'year', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium text-sm">KM</Label>
                      <Input className="px-4 py-2 rounded-lg" placeholder="KM" value={v.km} onChange={e => updateEditVehicle(idx, 'km', e.target.value)} />
                    </div>
                    {editClient?.isFleet && (
                      <>
                        <div className="space-y-2">
                          <Label className="font-medium text-sm">
                            N.º interno <span className="text-muted-foreground text-xs">(opcional)</span>
                          </Label>
                          <Input className="px-4 py-2 rounded-lg" placeholder="Ej: M-05" value={v.internalCode || ''} onChange={e => updateEditVehicle(idx, 'internalCode', e.target.value)} />
                        </div>
                        <div className="space-y-2 col-span-2">
                          <Label className="font-medium text-sm">
                            Contacto del vehículo <span className="text-muted-foreground text-xs">(opcional)</span>
                          </Label>
                          <Input className="px-4 py-2 rounded-lg" placeholder="Nombre del chofer / responsable" value={v.contactName || ''} onChange={e => updateEditVehicle(idx, 'contactName', e.target.value)} />
                        </div>
                        <div className="space-y-2 col-span-2">
                          <Label className="font-medium text-sm">
                            Teléfono del contacto <span className="text-muted-foreground text-xs">(opcional)</span>
                          </Label>
                          <Input className="px-4 py-2 rounded-lg" placeholder="3416858698" value={v.contactPhone || ''} onChange={e => updateEditVehicle(idx, 'contactPhone', e.target.value)} />
                        </div>
                      </>
                    )}
                    <Button type="button" variant="ghost" size="sm" className="text-red-600 hover:text-red-800 hover:bg-red-50 col-span-5" onClick={() => removeEditVehicle(idx)}>
                      <TrashIcon className="h-4 w-4 mr-2" />
                      Eliminar Vehculo
                    </Button>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="editAlsoProvider"
                    checked={editAlsoProvider}
                    onChange={(e) => setEditAlsoProvider(e.target.checked)}
                    className="rounded"
                  />
                  <Label htmlFor="editAlsoProvider" className="font-normal cursor-pointer">
                    También es proveedor
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Crea (o vincula por CUIT/nombre) la ficha en Proveedores. Las deudas entre las dos
                  cuentas corrientes se compensan automáticamente.
                </p>
              </div>

              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="editPideDatosRetiro"
                    checked={editPideDatosRetiro}
                    onChange={(e) => setEditPideDatosRetiro(e.target.checked)}
                    className="rounded"
                  />
                  <Label htmlFor="editPideDatosRetiro" className="font-normal cursor-pointer">
                    Pedir nombre y DNI de quien retira
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Al entregar un vehículo de este cliente hay que elegir quién lo retira (o cargar uno
                  nuevo). Queda en el remito, así no hace falta escanear el papel firmado.
                </p>
                {editPideDatosRetiro && editClient?.id && <PersonasRetiroLista clientId={editClient.id} />}
              </div>

              {editClient?.id && (
                <div className="mt-6 border-t pt-4">
                  <BillingProfiles clientId={editClient.id} />
                </div>
              )}
          <DialogFooter className="gap-3 pt-6">
            <Button type="button" variant="outline" onClick={() => { setShowEditModal(false); setEditClient(null); }}>
              Cancelar
            </Button>
            <Button type="submit" form="edit-client-form">
              <PencilIcon className="h-4 w-4 mr-2" />
              Guardar Cambios
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de detalles del cliente */}
      <Dialog open={showDetailModal} onOpenChange={(open) => {
        if (!open) {
            setShowDetailModal(false);
            setDetailClient(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {detailClient && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between gap-4 pr-12">
                  <DialogTitle className="text-2xl font-bold">{getClientName(detailClient)}</DialogTitle>
                  <div className="flex shrink-0 items-center gap-2">
                  {(detailClient.isFleet || detailClient.type === 'company' || (detailClient.vehiclesCount || 0) > 1) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setResumenCliente({ id: detailClient.id, nombre: getClientName(detailClient) })}
                      className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-sm font-medium"
                    >
                      <DocumentChartBarIcon className="h-4 w-4" />
                      Resumen mensual
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowDetailModal(false);
                      setDetailClient(null);
                      navigate(`/clients/${detailClient.id}/edit`);
                    }}
                    className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium h-8 rounded-lg gap-1.5 px-3 mr-2"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                  </div>
                </div>
                
                {/* Navigation Menu */}
                <div className="mt-4">
                  <NavigationMenu>
                    <NavigationMenuList>
                      <NavigationMenuItem>
                        <NavigationMenuLink asChild>
                          <button
                            onClick={() => setActiveSection('info')}
                            className={`cursor-pointer select-none rounded-sm px-3 py-1.5 text-sm font-medium outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
                              activeSection === 'info' ? 'bg-accent text-accent-foreground' : ''
                            }`}
                          >
                            Información
                          </button>
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                      <NavigationMenuItem>
                        <NavigationMenuLink asChild>
                          <button
                            onClick={() => setActiveSection('vehicles')}
                            className={`cursor-pointer select-none rounded-sm px-3 py-1.5 text-sm font-medium outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
                              activeSection === 'vehicles' ? 'bg-accent text-accent-foreground' : ''
                            }`}
                          >
                            Vehículos
                          </button>
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                      <NavigationMenuItem>
                        <NavigationMenuLink asChild>
                          <button
                            onClick={() => setActiveSection('appointments')}
                            className={`cursor-pointer select-none rounded-sm px-3 py-1.5 text-sm font-medium outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
                              activeSection === 'appointments' ? 'bg-accent text-accent-foreground' : ''
                            }`}
                          >
                            Turnos
                          </button>
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                      <NavigationMenuItem>
                        <NavigationMenuLink asChild>
                          <button
                            onClick={() => setActiveSection('budgets')}
                            className={`cursor-pointer select-none rounded-sm px-3 py-1.5 text-sm font-medium outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
                              activeSection === 'budgets' ? 'bg-accent text-accent-foreground' : ''
                            }`}
                          >
                            Presupuestos
                          </button>
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                      <NavigationMenuItem>
                        <NavigationMenuLink asChild>
                          <button
                            onClick={() => setActiveSection('services')}
                            className={`cursor-pointer select-none rounded-sm px-3 py-1.5 text-sm font-medium outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
                              activeSection === 'services' ? 'bg-accent text-accent-foreground' : ''
                            }`}
                          >
                            Servicios
                          </button>
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                      <NavigationMenuItem>
                        <NavigationMenuLink asChild>
                          <button
                            onClick={() => setActiveSection('history')}
                            className={`cursor-pointer select-none rounded-sm px-3 py-1.5 text-sm font-medium outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
                              activeSection === 'history' ? 'bg-accent text-accent-foreground' : ''
                            }`}
                          >
                            Historial
                          </button>
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                    </NavigationMenuList>
                  </NavigationMenu>
                </div>
              </DialogHeader>

              <div className="mt-6 space-y-6">
                {activeSection === 'info' && (
                  <>
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <ChartBarIcon className="h-5 w-5" />
                          Resumen del cliente
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="grid gap-4 md:grid-cols-2">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Facturación total</p>
                          <p className="text-sm font-semibold">{formatCurrency(getClientTotalSpent(detailClient))}</p>
                          <p className="text-xs text-muted-foreground">{getSpentRange(getClientTotalSpent(detailClient))}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Última visita</p>
                          <p className="text-sm font-semibold">{getLastVisitLabel(getClientLastVisitDate(detailClient))}</p>
                          <p className="text-xs text-muted-foreground">{formatShortDate(getClientLastVisitDate(detailClient))}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Cantidad de visitas</p>
                          <p className="text-sm font-semibold">{detailClient.visitCount ?? detailClient.serviceCount ?? 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Deuda / Saldo</p>
                          <p className="text-sm font-semibold">{formatCurrency(detailClient.debtBalance || 0)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Nivel de gasto</p>
                          <Badge className={getValueIndicator(getClientTotalSpent(detailClient)).className}>
                            {getValueIndicator(getClientTotalSpent(detailClient)).label}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Estado</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={getClientLevelBadge(getClientLevel(detailClient))}>
                              {getClientLevel(detailClient)}
                            </Badge>
                            <Badge className={getClientStatusBadge(getClientStatus(detailClient))}>
                              {getClientStatus(detailClient)}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Información del cliente */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <UserIcon className="h-5 w-5" />
                      Información del cliente
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Nombre</p>
                      <p className="text-sm font-semibold">{getClientName(detailClient)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Tipo</p>
                      <p className="text-sm">{detailClient.type === 'company' ? 'Empresa' : 'Individual'}</p>
                    </div>
                    {detailClient.category && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Categoría</p>
                        <Badge className={getCategoryColor(detailClient.category)}>
                          {getCategoryText(detailClient.category)}
                        </Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Información impositiva */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <DocumentTextIcon className="h-5 w-5" />
                      Información impositiva
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Documento</p>
                      <p className="text-sm font-semibold">{formatDocument(detailClient)}</p>
                    </div>
                    {detailClient.ivaCondition && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Condición IVA</p>
                        <p className="text-sm">
                          {detailClient.ivaCondition === 'responsable_inscripto' ? 'Responsable Inscripto' :
                           detailClient.ivaCondition === 'monotributista' ? 'Monotributista' :
                           detailClient.ivaCondition === 'exento' ? 'Exento' :
                           detailClient.ivaCondition === 'consumidor_final' ? 'Consumidor Final' :
                           detailClient.ivaCondition === 'no_responsable' ? 'No Responsable' :
                           detailClient.ivaCondition === 'responsable_no_inscripto' ? 'Responsable No Inscripto' :
                           detailClient.ivaCondition}
                        </p>
                      </div>
                    )}
                    {detailClient.taxId && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">ID Fiscal</p>
                        <p className="text-sm">{detailClient.taxId}</p>
                      </div>
                    )}
                    {detailClient.activityCode && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Código de actividad</p>
                        <p className="text-sm">{detailClient.activityCode}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Datos de contacto */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <EnvelopeIcon className="h-5 w-5" />
                      Datos de contacto
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Email</p>
                      <div className="flex items-center gap-2">
                        <EnvelopeIcon className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm">{detailClient.email || 'Sin email'}</p>
                      </div>
                    </div>
                    {detailClient.phone && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Teléfono</p>
                        <div className="flex items-center gap-2">
                          <svg className="h-4 w-4 text-muted-foreground" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                          </svg>
                          <a
                            href={`https://wa.me/${getWhatsappNumber(detailClient.phone)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            {detailClient.phone}
                          </a>
                        </div>
                      </div>
                    )}
                    {detailClient.whatsapp && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">WhatsApp</p>
                        <p className="text-sm">{detailClient.whatsapp}</p>
                      </div>
                    )}
                    {detailClient.instagram && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Instagram</p>
                        <div className="flex items-center gap-2">
                          <svg className="h-4 w-4 text-pink-500" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                          </svg>
                          <a
                            href={`https://instagram.com/${detailClient.instagram.replace(/^@/, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-pink-600 hover:text-pink-800 dark:text-pink-400 dark:hover:text-pink-300"
                          >
                            {detailClient.instagram}
                          </a>
                        </div>
                      </div>
                    )}
                    {detailClient.contactPerson && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Persona de contacto</p>
                        <p className="text-sm">{detailClient.contactPerson}</p>
                      </div>
                    )}
                    {detailClient.contactPhone && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Teléfono de contacto</p>
                        <p className="text-sm">{detailClient.contactPhone}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Domicilio */}
                {(detailClient.address || detailClient.street || detailClient.city) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <MapPinIcon className="h-5 w-5" />
                        Domicilio
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {(detailClient.address || detailClient.street) && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Dirección</p>
                          <p className="text-sm font-semibold">
                            {detailClient.address || `${detailClient.street || ''} ${detailClient.streetNumber || ''}`.trim()}
                          </p>
                        </div>
                      )}
                      {(detailClient.city || detailClient.province) && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Localidad</p>
                          <p className="text-sm">
                            {[detailClient.city, detailClient.province].filter(Boolean).join(', ')}
                            {detailClient.postalCode && ` - CP: ${detailClient.postalCode}`}
                          </p>
                        </div>
                      )}
                      {detailClient.country && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">País</p>
                          <p className="text-sm">{detailClient.country}</p>
                        </div>
                      )}
                      {(detailClient.address || detailClient.street || detailClient.city || detailClient.province) && (
                        <a
                          href={getGoogleMapsUrl(
                            detailClient.address || '',
                            detailClient.street || '',
                            detailClient.streetNumber || '',
                            detailClient.city || '',
                            detailClient.province || ''
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mt-2 hover:underline duration-200"
                        >
                          <MapPinIcon className="h-4 w-4" />
                          Buscar en Google Maps
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Información adicional */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <ClockIcon className="h-5 w-5" />
                      Información adicional
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start gap-2">
                      <CalendarIcon className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Cliente desde</p>
                        <p className="text-sm font-medium">{formatDate(detailClient.createdAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <ClockIcon className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Última modificación</p>
                        <p className="text-sm font-medium">{formatDate(detailClient.updatedAt)}</p>
                      </div>
                    </div>
                    {detailClient.birthDate && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Fecha de nacimiento</p>
                        <p className="text-sm">{formatDateOnly(detailClient.birthDate)}</p>
                      </div>
                    )}
                    {detailClient.notes && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Notas</p>
                        <p className="text-sm whitespace-pre-wrap">{detailClient.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                  </>
                )}

                {activeSection === 'vehicles' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <TruckIcon className="h-5 w-5" />
                        Vehículos ({clientVehicles.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {loadingSection ? (
                        <p className="text-sm text-muted-foreground">Cargando...</p>
                      ) : clientVehicles.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No hay vehículos registrados</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Marca/Modelo</TableHead>
                              <TableHead>Patente</TableHead>
                              <TableHead>Año</TableHead>
                              <TableHead>KM</TableHead>
                              <TableHead className="text-right">Historial</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {clientVehicles.map((vehicle) => (
                              <TableRow
                                key={vehicle.id || (vehicle as any)._id || (vehicle as any).vehicleId || vehicle.licensePlate}
                                className="hover:bg-muted/50"
                              >
                                <TableCell className="font-medium">
                                  {vehicle.make || vehicle.brand} {vehicle.model}
                                </TableCell>
                                <TableCell>{vehicle.licensePlate || '-'}</TableCell>
                                <TableCell>{vehicle.year || '-'}</TableCell>
                                <TableCell>{formatMileage(vehicle.mileage || vehicle.km)}</TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <Tip label="Ver historial del vehículo">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openVehicleHistoryFromClientModal(vehicle);
                                        }}
                                      >
                                        <ClockIcon className="h-4 w-4" />
                                      </Button>
                                    </Tip>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>
                )}

                {activeSection === 'appointments' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <CalendarIcon className="h-5 w-5" />
                        Turnos ({clientAppointments.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {loadingSection ? (
                        <p className="text-sm text-muted-foreground">Cargando...</p>
                      ) : clientAppointments.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No hay turnos registrados</p>
                      ) : (
                        <div className="space-y-3">
                          {clientAppointments.map((appointment: any) => (
                            <div key={appointment.id} className="border rounded-lg p-3">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-sm">{formatDate(appointment.scheduledDate)}</p>
                                  {appointment.service && <p className="text-xs text-muted-foreground mt-1">{appointment.service}</p>}
                                  {appointment.notes && <p className="text-xs text-muted-foreground mt-1">{appointment.notes}</p>}
                                </div>
                                <Badge variant={appointment.status === 'completed' ? 'default' : 'secondary'}>
                                  {appointment.status === 'completed' ? 'Completado' : appointment.status === 'pending' ? 'Pendiente' : appointment.status}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {activeSection === 'budgets' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <DocumentTextIcon className="h-5 w-5" />
                        Presupuestos ({clientBudgets.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {loadingSection ? (
                        <p className="text-sm text-muted-foreground">Cargando...</p>
                      ) : clientBudgets.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No hay presupuestos registrados</p>
                      ) : (
                        <div className="space-y-3">
                          {clientBudgets.map((budget: any) => (
                            <div key={budget.id} className="border rounded-lg p-3">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-sm font-mono">
                                    {budget.budgetNumber || budget.code || `Presupuesto ${String(budget.id || '').slice(0, 8).toUpperCase()}`}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">{formatDate(budget.createdAt)}</p>
                                  {budget.total && <p className="text-sm font-semibold mt-1">{formatCurrency(budget.total)}</p>}
                                </div>
                                <Badge variant={budget.status === 'approved' ? 'default' : budget.status === 'pending' ? 'secondary' : 'outline'}>
                                  {({
                                    approved: 'Aprobado',
                                    pending: 'Pendiente',
                                    sent: 'Enviado',
                                    rejected: 'Rechazado',
                                    expired: 'Vencido',
                                    cancelled: 'Anulado'
                                  } as Record<string, string>)[budget.status] || budget.status}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {activeSection === 'services' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <WrenchIcon className="h-5 w-5" />
                        Servicios realizados ({clientServiceReports.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {loadingSection ? (
                        <p className="text-sm text-muted-foreground">Cargando...</p>
                      ) : clientServiceReports.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No hay servicios registrados</p>
                      ) : (
                        <div className="space-y-3">
                          {clientServiceReports.map((report: any) => (
                            <div key={report.id} className="border rounded-lg p-3">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-sm font-mono">
                                    {report.appointmentCode || report.reportNumber || `Servicio ${String(report.id || '').slice(0, 8).toUpperCase()}`}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">{formatDate(report.createdAt)}</p>
                                  {report.workDescription && (
                                    <p className="text-xs text-muted-foreground mt-1">{report.workDescription}</p>
                                  )}
                                </div>
                                <Badge variant={report.status === 'completed' ? 'default' : 'secondary'}>
                                  {({
                                    completed: 'Completado',
                                    in_progress: 'En proceso',
                                    paused: 'Pausado',
                                    pending: 'Pendiente',
                                    delivered: 'Entregado',
                                    cancelled: 'Anulado'
                                  } as Record<string, string>)[report.status] || report.status}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {activeSection === 'history' && (
                  <>
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <ReceiptRefundIcon className="h-5 w-5" />
                          Historial de visitas ({clientRemitos.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {loadingSection ? (
                          <p className="text-sm text-muted-foreground">Cargando...</p>
                        ) : clientRemitos.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No hay visitas registradas</p>
                        ) : (
                          <div className="space-y-3">
                            {clientRemitos.map((remito: any) => (
                              <div key={remito.id} className="border rounded-lg p-3">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium text-sm font-mono">
                                      {remito.remitNumber || remito.number || `Remito ${String(remito.id || '').slice(0, 8).toUpperCase()}`}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">{formatDate(remito.date || remito.createdAt)}</p>
                                    {remito.total && <p className="text-sm font-semibold mt-1">{formatCurrency(remito.total)}</p>}
                                  </div>
                                  <Badge variant={remito.status === 'delivered' ? 'default' : 'secondary'}>
                                    {({
                                      delivered: 'Entregado',
                                      issued: 'Emitido',
                                      pending: 'Pendiente',
                                      draft: 'Borrador',
                                      cancelled: 'Anulado'
                                    } as Record<string, string>)[remito.status] || remito.status}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <DocumentTextIcon className="h-5 w-5" />
                          Facturas ({clientInvoices.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {loadingSection ? (
                          <p className="text-sm text-muted-foreground">Cargando...</p>
                        ) : clientInvoices.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No hay facturas registradas</p>
                        ) : (
                          <div className="space-y-3">
                            {clientInvoices.map((invoice: any) => (
                              <div key={invoice.id} className="border rounded-lg p-3">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium text-sm">Factura #{invoice.number || invoice.id}</p>
                                    <p className="text-xs text-muted-foreground mt-1">{formatDate(invoice.date || invoice.createdAt)}</p>
                                    {invoice.total && <p className="text-sm font-semibold mt-1">{formatCurrency(invoice.total)}</p>}
                                  </div>
                                  <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'}>
                                    {invoice.status === 'paid' ? 'Pagada' : invoice.status === 'pending' ? 'Pendiente' : invoice.status}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de subida de CSV */}
      <Dialog open={showUploadModal} onOpenChange={(open) => !open && setShowUploadModal(false)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Subir Archivo CSV de Clientes</DialogTitle>
          </DialogHeader>

            <div className="space-y-6">
              {/* Seccin de archivo */}
              <div className="space-y-2">
                <Label htmlFor="csv-file" className="font-medium text-sm">Archivo CSV</Label>
                <Input
                  id="csv-file"
                  type="file"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  className="px-4 py-3 rounded-lg cursor-pointer"
                />
              </div>

              {/* Botones de accion */}
              <div className="flex justify-between items-center gap-3">
                <Button
                  type="button"
                  onClick={() => downloadTemplate()}
                  variant="outline"
                  className="flex-1"
                >
                  <ArrowUpTrayIcon className="h-4 w-4 mr-2 rotate-180" />
                  Descargar Plantilla
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowUploadModal(false)}
                >
                  Cancelar
                </Button>
              </div>
              
              {/* Información del formato - Colapsable */}
              <details className="rounded-lg bg-muted">
                <summary className="p-3 cursor-pointer font-medium hover:bg-opacity-80">
                  ? Ver formato del CSV y campos disponibles
                </summary>
                <div className="p-3 pt-0 border-t border-gray-400 border-opacity-30">
                  <div className="space-y-3">
                    <div>
                      <h5 className="font-medium mb-2">Campos requeridos:</h5>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><strong>tipo</strong> - individual o company</div>
                        <div><strong>email</strong> - Email (requerido)</div>
                        <div><strong>telefono</strong> - Telefono (requerido)</div>
                      </div>
                    </div>
                    
                    <div>
                      <h5 className="font-medium mb-2">Campos para clientes individuales:</h5>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><strong>nombre</strong> - Nombre (requerido)</div>
                        <div><strong>apellido</strong> - Apellido (requerido)</div>
                        <div><strong>fechaNacimiento</strong> - Fecha (YYYY-MM-DD)</div>
                        <div><strong>dni</strong> - DNI</div>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium mb-2">Campos para empresas:</h5>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><strong>nombreEmpresa</strong> - Nombre (requerido)</div>
                        <div><strong>cuit</strong> - CUIT</div>
                        <div><strong>formaLegal</strong> - Forma legal</div>
                        <div><strong>fechaInicioActividad</strong> - Fecha (YYYY-MM-DD)</div>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium mb-2">Campos opcionales:</h5>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><strong>whatsapp</strong> - WhatsApp</div>
                        <div><strong>direccion</strong> - Direccion completa</div>
                        <div><strong>ciudad</strong> - Ciudad</div>
                        <div><strong>provincia</strong> - Provincia</div>
                        <div><strong>condicionIva</strong> - Condición IVA</div>
                        <div><strong>categoria</strong> - nuevo, frecuente, vip, inactivo</div>
                      </div>
                    </div>
                    
                    <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                      <p className="text-xs text-blue-800 dark:text-blue-200">
                        ? <strong>Consejo:</strong> Usa "Descargar Plantilla" para obtener un archivo CSV de ejemplo
                      </p>
                    </div>
                    
                    <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800">
                      <p className="text-xs text-yellow-800 dark:text-yellow-200">
                        ? <strong>Excel:</strong> Doble clic en el archivo CSV o arrastra directamente a Excel
                      </p>
                    </div>
                  </div>
                </div>
              </details>
            </div>
        </DialogContent>
      </Dialog>

      {/* Modal historial del vehículo */}
      {selectedVehicleForHistory && (
        <VehicleHistoryModal
          vehicle={selectedVehicleForHistory}
          clientName={detailClient ? getClientName(detailClient) : ''}
          isOpen={vehicleHistoryOpen}
          onClose={() => {
            setVehicleHistoryOpen(false);
            setSelectedVehicleForHistory(null);
          }}
        />
      )}

      <ClientWhatsAppChatDialog
        open={showWhatsAppChat}
        onOpenChange={setShowWhatsAppChat}
        client={
          chatClient
            ? {
                id: chatClient.id,
                firstName: chatClient.firstName,
                lastName: chatClient.lastName,
                companyName: chatClient.companyName,
                phone: getChatPhone(chatClient)
              }
            : null
        }
      />
      <ResumenFlotaDialog
        clientId={resumenCliente?.id || null}
        clientName={resumenCliente?.nombre}
        open={!!resumenCliente}
        onOpenChange={(v) => { if (!v) setResumenCliente(null); }}
      />
    </div>
  );
};

export default Clients; 
